// third-party
import { createSlice } from '@reduxjs/toolkit';
import { devConsolelog } from 'helpers/utilityFunctions';
import { calculateNetAmount } from 'helper';
// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { dispatch } from 'store';
import axios from 'utils/axios';
import { DEFAULT_ERROR_MESSAGE } from '../constant';
import { openSnackbar } from './snackbar';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    utilities: {},
    autoSuggestData: {
        buyerDropdownData: [],
        requirementDropdownData: [],
        quoteDropdownData: []
    },
    isLoading: false
};

const slice = createSlice({
    name: 'pendingBooking',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        setIsLoading(state, action) {
            const { loadingField = '', isLoading = false } = action.payload || {};
            state[loadingField] = isLoading;
        },

        setUtilities(state, action) {
            const { results = {} } = action.payload || {};
            state.utilities = results?.utilities;
        },

        setBuyerAutosuggestData(state, action) {
            devConsolelog('auto suggest data ', state, action.payload);
            const {
                code__completion: memCodeData = [],
                company_name__completion: companyNameData = [],
                email__completion: emailData = [],
                name__completion: nameData = []
            } = action.payload;

            const autosuggestData = [
                memCodeData[0]?.options,
                nameData[0]?.options,
                companyNameData[0]?.options,
                emailData[0]?.options
            ].find((arr) => arr.length);

            const autosuggestDropdownData = autosuggestData.map((data) => {
                const { _source: source = {} } = data || {};
                const { name = '', code = '', email = '', company_name: companyName = '', id = '' } = source || {};

                return { code, name, email, companyName, id, label: `${companyName} - ${email}` };
            });

            state.autoSuggestData.buyerDropdownData = autosuggestDropdownData;
        },

        setRequirementAutosuggestData(state, action) {
            const { response = [] } = action.payload;

            const autosuggestDropdownData = response.map((data) => {
                const { code = '', id = '', member_list: memberList = {}, departure_at: departureAt = '' } = data || {};

                return { code, id, label: `#${id} - ${code} - ${memberList?.title} on ${departureAt}` };
            });

            state.autoSuggestData.requirementDropdownData = autosuggestDropdownData;
        },

        setQuoteAutosuggestData(state, action) {
            // const { response = [] } = action.payload;

            devConsolelog('quotes_autosuggest_data', action.payload);
            const autosuggestDropdownData = action.payload.map((item, index) => {
                const {
                    code = '',
                    id = '',
                    currency = {},
                    destination = {},
                    nights_count: nightsCount = 0,
                    price = 0,
                    title = '',
                    category = {},
                    adults_count: adultsCount = null,
                    customer_info_requirement: customerInfoRequirement = [],
                    children_count: childrenCount = 0,
                    buyer = {}
                } = item || {};
                // return { code, id, label: `#${id} - ${code} - ${memberList?.title} on ${departureAt}` };
                const leadPaxDetails = customerInfoRequirement?.[0] || {};
                return {
                    id,
                    code,
                    currency,
                    destination,
                    nightsCount,
                    price,
                    title,
                    category,
                    adultsCount,
                    childrenCount,
                    buyer,
                    leadPaxDetails
                };
            });

            state.autoSuggestData.quoteDropdownData = autosuggestDropdownData;
        }
    }
});

// Reducer
export default slice.reducer;

export function getBookingUtilities() {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingUtilitiesLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/bookings/utilities/`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setUtilities({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingUtilitiesLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );

            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingUtilitiesLoading', isLoading: false }));
        }
    };
}

export function getBuyerAutosuggestData(searchedVal = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/member/suggest/?code__completion=${searchedVal}&company_name__completion=${searchedVal}&name__completion=${searchedVal}&email__completion=${searchedVal}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getBuyerAutosuggestData', response.data);
            dispatch(slice.actions.setBuyerAutosuggestData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getRequirementAutosuggestData(reqCode = '') {
    const url = `${ARYABHATTA_API_DOMAIN_V1}lms/requirements/auto-suggest/suggest/?code__completion=${reqCode}`;
    return async () => {
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = response || {};
            dispatch(slice.actions.setRequirementAutosuggestData({ ...data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getQuoteAutosuggestData(reqCode = '', formik = {}) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}acm/requirements/${reqCode}/quotes?booking_summary_edit=true`;
    return async () => {
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = response || {};
            devConsolelog('quotes_data', data);
            const { customer_info_requirement: customerInfoRequirement = [] } = data[0] || {};
            const leadPaxDetails = customerInfoRequirement?.[0] || {};
            formik.setFieldValue('pax_title', leadPaxDetails?.title || '');
            formik.setFieldValue('first_name', leadPaxDetails?.first_name || '');
            formik.setFieldValue('last_name', leadPaxDetails?.last_name || '');
            devConsolelog('quotes_data1', leadPaxDetails, customerInfoRequirement);
            dispatch(slice.actions.setQuoteAutosuggestData(data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}
export function getQuoteAutosuggestPrefillData(quoteCode = '', formik = {}, setIsDataPrefilled = () => {}) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/quote/${quoteCode}`;
    return async () => {
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = response || {};
            const { response: prefillResponse = {} } = data || {};
            setIsDataPrefilled(true);
            formik.setFieldValue('nights_count', prefillResponse?.numberOfNights || '');
            formik.setFieldValue('title', prefillResponse?.title || '');
            formik.setFieldValue('amount', prefillResponse?.price || '');
            formik.setFieldValue('adults_count', prefillResponse?.totalAdultCount || null);
            formik.setFieldValue('children_count', prefillResponse?.totalChildCount || 0);
            formik.setFieldValue('infant_count', prefillResponse?.totalInfantCount || 0);
            formik.setFieldValue('departure_at', prefillResponse?.dateOfTravel || null);
            formik.setFieldValue('discount', prefillResponse?.discountAndSurcharges?.discount || null);
            formik.setFieldValue('discountRemark', prefillResponse?.discountAndSurcharges?.discountRemark || null);
            formik.setFieldValue('surcharge', prefillResponse?.discountAndSurcharges?.surcharge || null);
            formik.setFieldValue('surchargeRemark', prefillResponse?.discountAndSurcharges?.surchargeRemark || null);
            if (
                prefillResponse?.price &&
                prefillResponse?.code &&
                (prefillResponse?.discountAndSurcharges?.discount != null || prefillResponse?.discountAndSurcharges?.surcharge != null)
            ) {
                const totalNet = calculateNetAmount(
                    prefillResponse.price,
                    prefillResponse?.discountAndSurcharges?.discount || 0,
                    prefillResponse?.discountAndSurcharges?.surcharge || 0
                );

                formik.setFieldValue('totalnetpay', totalNet);
            } else {
                formik.setFieldValue('totalnetpay', prefillResponse?.price || '');
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            formik.setFieldValue('quote_code', { id: '', code: '' });
            formik.setFieldValue('destination', '');
            // formik.setFieldValue('category', '');
            formik.setFieldValue('currency', '');
            formik.setFieldValue('nights_count', '');
            formik.setFieldValue('title', '');
            formik.setFieldValue('amount', '');
            formik.setFieldValue('totalnetpay', '');
            formik.setFieldValue('adults_count', null);
            formik.setFieldValue('children_count', 0);
            formik.setFieldValue('infant_count', 0);
            formik.setFieldValue('departure_at', null);
            formik.setFieldValue('discount', null);
            formik.setFieldValue('discountRemark', null);
            formik.setFieldValue('surcharge', null);
            formik.setFieldValue('surchargeRemark', null);
            setIsDataPrefilled(false);
        }
    };
}

export function postPendingBooking(bookingData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/bookings/`;
        try {
            const response = await axios.post(url, bookingData, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Pending Booking Created`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );

            const { code = '' } = response?.data || {};
            if (code) {
                window.open(`booking-list/${code}`, '_blank');
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export const setLoadingState =
    (isLoading = false) =>
    () => {
        dispatch(slice.actions.setIsLoading(isLoading));
    };
